import { Box, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { Table } from 'Common/MUITableV2'
import { GridSkeleton } from './GridSkeleton.view'
import { autoHeight } from 'Common/utils/utils'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { formatNumber } from './Utilisation.data'
import {
  aggregateKitchen,
  GanttRow,
  KitchenAggRow,
  toDetails,
} from './Utilisation.derive'

type Props = {
  schedulerId: string
  iteration: string
}

const numCol = (
  field: keyof KitchenAggRow,
  headerName: string,
  digits = 2
): GridColDef<KitchenAggRow> => ({
  field: field as string,
  headerName,
  type: 'number',
  flex: 1,
  minWidth: 130,
  valueFormatter: (value: number) => formatNumber(value, digits),
})

const COLS: GridColDef<KitchenAggRow>[] = [
  { field: 'kitchen', headerName: 'Kitchen', flex: 1, minWidth: 100 },
  {
    field: 'inputCode_desc',
    headerName: 'Input Code Desc',
    flex: 2,
    minWidth: 220,
  },
  numCol('fg_cases', 'FG Cases', 0),
  numCol('input_material_kg', 'Input Material KG', 1),
  numCol('max_kitchen_clean_end_time', 'Max Kitchen Clean End', 2),
  numCol('sum_kitchen_run_time', 'Sum Kitchen Run Time', 2),
  numCol('sum_kitchen_run_time_unique', 'Sum Kitchen Run Time (Unique)', 2),
  numCol('sum_kitchen_clean_time', 'Sum Kitchen Clean Time', 2),
  numCol('sum_line_run_time', 'Sum Line Run Time', 2),
  numCol('sum_line_clean_time', 'Sum Line Clean Time', 2),
]

export const KitchenUtilisationView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useSchedulerData(schedulerId, iteration)

  const rows = useMemo<KitchenAggRow[]>(() => {
    const gantt = (data?.data ?? []) as GanttRow[]
    return aggregateKitchen(toDetails(gantt))
  }, [data])

  // Grid skeleton, not a spinner: same columns, same height, so the
  // real table replaces it without the layout moving.
  if (isLoading) return <GridSkeleton columns={COLS as GridColDef[]} />
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load schedule data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="table-container-mui">
      <Table
        ref={(el: HTMLDivElement | null) => el && autoHeight(el)}
        rows={rows}
        columns={COLS as GridColDef[]}
        density="compact"
        disableRowSelectionOnClick
        getRowClassName={(p) =>
          (p.indexRelativeToCurrentPage ?? 0) % 2 === 0
            ? 'row--even'
            : 'row--odd'
        }
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  )
}
